// A30 — Settings → Billing tab.
//
// Reads /billing/subscription. Free-tier users see "Subscribe" → Stripe
// Checkout. Active subscribers see "Manage subscription" → Stripe
// Customer Portal. Past-due subscribers see a banner but functionality
// stays active per the dunning policy in pricing_and_tiers.md.
//
// Premium subscribers also see a lifetime-savings line when the savings
// ledger has data (requires the Settings panel context to know which
// workspace's savings to read; for now we render only the subscription
// state and defer the savings line to the Usage tab).

import { useEffect, useState } from "react";
import {
  ApiError,
  createBillingCheckout,
  createBillingPortal,
  getBillingSubscription,
  type SubscriptionInfo,
} from "../api/client";

export function BillingPanel() {
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"checkout" | "portal" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getBillingSubscription()
      .then((s) => {
        if (!cancelled) setSub(s);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subscribe = async () => {
    setBusy("checkout");
    setError(null);
    try {
      const { checkout_url } = await createBillingCheckout();
      window.location.assign(checkout_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("portal");
    setError(null);
    try {
      const { portal_url } = await createBillingPortal();
      window.location.assign(portal_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <p className="text-xs text-muted" data-testid="billing-panel-loading">
        Loading subscription…
      </p>
    );
  }

  const isPremium = sub?.tier === "premium";
  const isPastDue = sub?.status === "past_due";
  const isCancellingAtPeriodEnd =
    isPremium && sub?.cancel_at_period_end === true;
  const periodEnd =
    sub?.current_period_end && sub.current_period_end > 0
      ? new Date(sub.current_period_end / 1000).toLocaleDateString()
      : null;

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-4"
      data-testid="billing-panel"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-primary">Subscription</h3>
        <span className="font-code text-[0.625rem] text-faint">
          {sub?.tier ?? "free"}
          {sub?.status ? ` · ${sub.status}` : ""}
        </span>
      </div>

      {isPastDue && (
        <p
          className="rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid="billing-panel-past-due-banner"
        >
          Payment failed — Stripe is retrying. Your subscription stays active
          for now.
        </p>
      )}

      {isCancellingAtPeriodEnd && periodEnd && (
        <p className="rounded-md border border-edge bg-surface px-3 py-2 text-xs text-muted">
          Cancelling on {periodEnd}. Premium features stay active until then.
        </p>
      )}

      {isPremium ? (
        <>
          <p className="text-xs text-muted">
            Premium tier active. Pooled models, higher sandbox caps, persona
            marketplace.
            {periodEnd && !isCancellingAtPeriodEnd && (
              <> Renews on {periodEnd}.</>
            )}
          </p>
          <button
            type="button"
            onClick={() => void manage()}
            disabled={busy !== null}
            className="self-start rounded-md border border-edge px-3 py-1.5 text-xs font-medium text-primary hover:border-accent hover:text-accent disabled:opacity-50"
            data-testid="billing-panel-manage"
          >
            {busy === "portal" ? "Opening portal…" : "Manage subscription"}
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-muted">
            Free tier — system agents (Vela, Pearl, Shore) covered by Waytide;
            user personas need a BYOK key. Subscribe to Premium for pooled
            models, higher compute caps, warm-pool sandboxes, and the persona
            marketplace.
          </p>
          <button
            type="button"
            onClick={() => void subscribe()}
            disabled={busy !== null}
            className="self-start rounded-md border border-accent bg-accent/15 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/25 disabled:opacity-50"
            data-testid="billing-panel-subscribe"
          >
            {busy === "checkout" ? "Opening checkout…" : "Subscribe to Premium"}
          </button>
        </>
      )}

      {error && (
        <p className="text-xs text-danger" data-testid="billing-panel-error">
          {error}
        </p>
      )}
    </div>
  );
}
